import { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const CommissionNfsaTdsPtaxDetails = ({
  month,
  year,
  allotMonth,
  allotYear,
  distMonthName,
  alotMonthName,
  distCode,
  userRole,
}) => {
  const [loading, setLoading] = useState([]);
  const [nfsaAbsCommission, setNfsaAbsCommission] = useState([]);
  const [commissionData, setCommissionData] = useState([]);
  const [error, setError] = useState(null);
  const [showButtonData, setShowButtonData] = useState(null);
  const [submitMessage, setSubmitMessage] = useState("");
  const allowedRoles = ["1", "9", "10"];

  useEffect(() => {
    const fetCommissionCost = async () => {
      try {
        setLoading(true);
        const commissioncostresponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/commission-cost`
        );

        if (!commissioncostresponse.ok) {
          throw new Error("Failed to fetch State commission data");
        }

        const data = await commissioncostresponse.json();
        setCommissionData(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetCommissionCost();
  }, []);

  useEffect(() => {
    const fetchNFSACommissionAbstractDetails = async () => {
      try {
        setSubmitMessage("");
        setLoading(true);
        const nfsaCommissionResponse = await fetch(
          `${process.env.REACT_APP_URL}/api/commission/nfsa-commission-abstract?month=${month}&year=${year}&alotMonth=${allotMonth}&alotYear=${allotYear}&distCode=${distCode}`
        );
        if (!nfsaCommissionResponse.ok) {
          throw new Error("Failed to fetch nfsa commission abstract");
        }
        const data = await nfsaCommissionResponse.json();
        setNfsaAbsCommission(data);
      } catch (error) {
        console.error("Fetch Error:", error);
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchNFSACommissionAbstractDetails();
  }, [month, year, allotMonth, allotYear, distCode]);

  const fetchCommissionStatus = async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        month,
        year,
        allotMonth,
        allotYear,
        distCode,
        userRole,
        nfsaStateFlag: "NFSA",
      });
      const commissionStatusResponse = await fetch(
        `${process.env.REACT_APP_URL}/api/commission/commision-status?${queryParams}`
      );

      if (!commissionStatusResponse.ok) {
        throw new Error("Failed to fetch NFSA commission status data");
      }

      const data = await commissionStatusResponse.json();
      setShowButtonData(data);
    } catch (error) {
      console.error("Fetch Error:", error);
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!distCode || distCode === "0") {
      return;
    }
    fetchCommissionStatus();
  }, [distCode, month, year, allotMonth, allotYear, userRole]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex" }}>
          <CircularProgress />
        </Box>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const distName = nfsaAbsCommission[0]?.distNameEn || "Unknown";
    const nfsaStateStatus = "NFSA";
    const payload = new URLSearchParams({
      month,
      year,
      allotMonth,
      allotYear,
      distCode,
      userRole,
      distMonthName,
      alotMonthName,
      distName,
      nfsaStateStatus,
    });

    try {
      const response = await fetch(
        `${process.env.REACT_APP_URL}/api/commission/update-commission-timestamp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: payload.toString(),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit verification.");
      }

      const data = await response.text();
      setSubmitMessage(data);
      if (!distCode || distCode !== "0") {
        fetchCommissionStatus();
      }
    } catch (error) {
      console.error("Submit Error:", error);
      setSubmitMessage("Submission failed. Please try again.");
    }
  };

  const totals = nfsaAbsCommission.reduce(
    (acc, item) => {
      acc.riceQty += Number(item.riceQty) || 0;
      acc.commissionAmount += Number(item.commissionAmount) || 0;
      acc.totalImpds += Number(item.totalImpds) || 0;
      acc.totalDistributedQty += Number(item.totalDistributedQty) || 0;
      acc.amountRecoveredAmount += Number(item.amountRecoveredAmount) || 0;
      acc.netPayableAmount += Number(item.netPayableAmount) || 0;
      acc.tds += Number(item.tds) || 0;
      acc.ptax += Number(item.ptax) || 0;
      acc.sbiFinal += Number(item.sbiFinal) || 0;
      acc.nonSbiFinal += Number(item.nonSbiFinal) || 0;
      acc.finalCommission += Number(item.finalCommission) || 0;
      acc.count += 1;
      return acc;
    },
    {
      riceQty: 0,
      commissionAmount: 0,
      totalImpds: 0,
      totalDistributedQty: 0,
      amountRecoveredAmount: 0,
      netPayableAmount: 0,
      tds: 0,
      ptax: 0,
      sbiFinal: 0,
      nonSbiFinal: 0,
      finalCommission: 0,
      count: 0,
    }
  );

  if (nfsaAbsCommission.length === 0) {
    return (
      <div className="mainhead" align="center">
        No data found
      </div>
    );
  }

  if (submitMessage != null) {
    <div className="mainhead" align="center" style={{ textDecoration: "none" }}>
      {submitMessage}
    </div>;
  }

  const handleDownloadExcel = async (data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("NFSA Commission Abstract");

    const totalColumns = 13;

    // Add the report title row with colspan (merge cells)
    worksheet.mergeCells(1, 1, 1, totalColumns); // Merge row 1, cols 1 to 21
    const titleCell = worksheet.getCell("A1");
    titleCell.value =
      "Monthly FPS Dealer Commission Report (SNA-SPARSH NFSA Format)";
    titleCell.font = { size: 12, bold: true };
    titleCell.alignment = { horizontal: "center", vertical: "middle" };

    // Define header row
    const headers = [
      "S.No",
      "District",
      "NFSA Qty of Rice Distributed (A)",
      `Amount Payable Towards Commission  (In Rs ${
        commissionData.nfsaCost || 0
      } per kg)  ( B = A * ${commissionData.nfsaCost || 0})`,
      `Total No of IMPDS  ${distMonthName} - ${year}`,
      `Total Qty Distributed(Kgs) under NFSA  ${distMonthName} - ${year}`,
      "Amount Recovered (C)",
      "Net Amount Payable (D = B-C)",
      "TDS",
      "Ptax",
      "SBI Final Commission",
      "Non-SBI Final Commission",
      "Final Commission",
    ];
    worksheet.addRow(headers);

    // Add data rows starting from row 3
    nfsaAbsCommission.forEach((item, index) => {
      const row = worksheet.addRow([
        index + 1,
        item.distNameEn,
        parseFloat(item.riceQty.toFixed(3)),
        parseFloat(item.commissionAmount.toFixed(2)),
        parseFloat(item.totalImpds),
        parseFloat(item.totalDistributedQty.toFixed(3)),
        parseFloat(item.amountRecoveredAmount.toFixed(2)),
        parseFloat(item.netPayableAmount.toFixed(2)),
        parseFloat(item.tds.toFixed(2)),
        parseFloat(item.ptax.toFixed(2)),
        parseFloat(item.sbiFinal.toFixed(2)),
        parseFloat(item.nonSbiFinal.toFixed(2)),
        parseFloat(item.finalCommission.toFixed(2)),
      ]);

      // Left align first 10 columns
      for (let i = 1; i <= 2; i++) {
        row.getCell(i).alignment = { horizontal: "left" };
      }
      // Right align columns 11 to 19
      for (let i = 3; i <= 13; i++) {
        row.getCell(i).alignment = { horizontal: "right" };
      }
    });

    // Apply styles to header (row 2)
    const headerRow = worksheet.getRow(2);
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: "center" };
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFDDDDDD" },
      };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    // Calculate totals manually first
    const totals = nfsaAbsCommission.reduce(
      (acc, item) => {
        acc.riceQty += Number(item.riceQty.toFixed(3)) || 0;
        acc.commissionAmount += Number(item.commissionAmount.toFixed(2)) || 0;
        acc.totalImpds += Number(item.totalImpds) || 0;
        acc.totalDistributedQty +=
          Number(item.totalDistributedQty.toFixed(3)) || 0;
        acc.amountRecoveredAmount +=
          Number(item.amountRecoveredAmount.toFixed(2)) || 0;
        acc.netPayableAmount += Number(item.netPayableAmount.toFixed(2)) || 0;
        acc.tds += Number(item.tds.toFixed(2)) || 0;
        acc.ptax += Number(item.ptax.toFixed(2)) || 0;
        acc.sbiFinal += Number(item.sbiFinal.toFixed(2)) || 0;
        acc.nonSbiFinal += Number(item.nonSbiFinal.toFixed(2)) || 0;
        acc.finalCommission += Number(item.finalCommission.toFixed(2)) || 0;
        acc.count += 1;
        return acc;
      },
      {
        riceQty: 0,
        commissionAmount: 0,
        totalImpds: 0,
        totalDistributedQty: 0,
        amountRecoveredAmount: 0,
        netPayableAmount: 0,
        tds: 0,
        ptax: 0,
        sbiFinal: 0,
        nonSbiFinal: 0,
        finalCommission: 0,
        count: 0,
      }
    );

    // Add total row below the last data row
    const totalRowNumber = worksheet.lastRow.number + 1;

    // Merge first 10 columns for 'Total' label
    worksheet.mergeCells(totalRowNumber, 1, totalRowNumber, 10);

    // Add total values row
    const totalRow = worksheet.addRow([
      "",
      "Total",
      totals.riceQty.toFixed(3),
      totals.commissionAmount.toFixed(2),
      totals.totalImpds,
      totals.totalDistributedQty.toFixed(3),
      totals.amountRecoveredAmount.toFixed(3),
      totals.netPayableAmount.toFixed(2),
      totals.tds.toFixed(2),
      totals.ptax.toFixed(2),
      totals.sbiFinal.toFixed(2),
      totals.nonSbiFinal.toFixed(2),
      totals.finalCommission.toFixed(2),
    ]);

    // Style total row
    totalRow.font = { bold: true };
    totalRow.alignment = { horizontal: "right" };
    totalRow.getCell(1).alignment = { horizontal: "left" };

    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "double" },
        right: { style: "thin" },
      };
      // Keep alignment consistent for merged cells
      if (colNumber <= 2) {
        cell.alignment = { horizontal: "left" };
      }
    });

    // Auto width adjustment for columns
    worksheet.columns.forEach((col, index) => {
      let maxLength = 10; // minimum width to avoid super narrow columns
      const headerText = headers[index] || "";
      if (headerText.length > maxLength) maxLength = headerText.length;

      col.eachCell({ includeEmpty: true }, (cell) => {
        let cellValue = cell.value;

        // If cell value is an object (like a Date), convert to string
        if (cellValue && typeof cellValue === "object") {
          if (cellValue.richText) {
            cellValue = cellValue.richText.map((rt) => rt.text).join("");
          } else if (cellValue.text) {
            cellValue = cellValue.text;
          } else if (cellValue.result) {
            cellValue = cellValue.result.toString();
          } else {
            cellValue = cellValue.toString();
          }
        } else if (cellValue === null || cellValue === undefined) {
          cellValue = "";
        } else {
          cellValue = cellValue.toString();
        }

        if (cellValue.length > maxLength) {
          maxLength = cellValue.length;
        }
      });

      // Optionally limit max width so columns don't get huge
      const maxAllowedWidth = 50;
      col.width = maxLength > maxAllowedWidth ? maxAllowedWidth : maxLength + 2;
    });

    // Save and trigger download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `NFSA_Wise_Commission.xlsx`);
  };

  return (
    <>
      {submitMessage && (
        <div
          className="mainhead"
          align="center"
          style={{
            textDecoration: "none",
            marginTop: "5px",
            marginBottom: "20px",
          }}
        >
          {submitMessage}
        </div>
      )}

      <div style={{ justifyContent: "center", marginBottom: "70px" }}>
        <table width="90%" border="1" cellPadding="5" cellSpacing="0">
          <thead>
            <tr className="tableheader">
              <th colSpan={13}>
                Monthly FPS Dealer Commission Report (SNA-SPARSH NFSA Format)
              </th>
            </tr>
            <tr className="tableheader">
              <th>S.No</th>
              <th>District</th>
              <th>
                NFSA <br /> Qty of Rice Distributed{" "}
                <font style={{ color: "orange" }}>(A)</font>
              </th>
              <th>
                Amount Payable Towards Commission <br /> (In Rs{" "}
                {commissionData.nfsaCost || 0} per kg) <br />{" "}
                <font style={{ color: "orange" }}>
                  (B=A*{commissionData.nfsaCost || 0})
                </font>
              </th>
              <th>
                Total No of IMPDS <br /> {distMonthName} - {year}
              </th>
              <th>
                Total Qty Distributed(Kgs) under NFSA <br /> {distMonthName} -{" "}
                {year}
              </th>
              <th>
                Amount Recovered <font style={{ color: "orange" }}>(C)</font>
              </th>
              <th>
                Net Amount Payable <br />
                <font style={{ color: "orange" }}>(D = B-C)</font>
              </th>
              <th>TDS</th>
              <th>Ptax</th>
              <th>SBI Final Commission</th>
              <th>Non-SBI Final Commission</th>
              <th>Final Commission</th>
            </tr>
          </thead>
          <tbody>
            {nfsaAbsCommission.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>{item.distNameEn}</td>
                <td style={{ textAlign: "right" }}>
                  {item.riceQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {" "}
                  {item.commissionAmount.toFixed(2)}
                </td>
                <td style={{ textAlign: "right" }}>{item.totalImpds}</td>
                <td style={{ textAlign: "right" }}>
                  {item.totalDistributedQty.toFixed(3)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.amountRecoveredAmount.toFixed(2)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.netPayableAmount.toFixed(2)}
                </td>
                <td style={{ textAlign: "right" }}>{item.tds.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>{item.ptax.toFixed(2)}</td>
                <td style={{ textAlign: "right" }}>
                  {item.sbiFinal.toFixed(2)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.nonSbiFinal.toFixed(2)}
                </td>
                <td style={{ textAlign: "right" }}>
                  {item.finalCommission.toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: "bold" }} className="tableheader">
              <td colSpan={2} style={{ textAlign: "right" }}>
                Total
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.riceQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.commissionAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>{totals.totalImpds}</td>
              <td style={{ textAlign: "right" }}>
                {totals.totalDistributedQty.toFixed(3)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.amountRecoveredAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.netPayableAmount.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>{totals.tds.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>{totals.ptax.toFixed(2)}</td>
              <td style={{ textAlign: "right" }}>
                {totals.sbiFinal.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.nonSbiFinal.toFixed(2)}
              </td>
              <td style={{ textAlign: "right" }}>
                {totals.finalCommission.toFixed(2)}
              </td>
            </tr>
          </tfoot>
        </table>
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          {
            <button
              onClick={() => handleDownloadExcel(nfsaAbsCommission)}
              className="button button2"
            >
              Download Excel
            </button>
          }

          { allowedRoles.includes(userRole) && distCode &&
            distCode !== "0" &&
            (showButtonData?.status === -1 ? (
              <>
                <br />
                <br />
                <div className="mainhead" style={{ textDecoration: "none" }}>
                  {showButtonData?.message}
                </div>
              </>
            ) : showButtonData?.showForm ? (
              <form onSubmit={handleSubmit} style={{ display: "inline" }}>
                <input type="hidden" name="month" value={month} />
                <input type="hidden" name="year" value={year} />
                <input type="hidden" name="alotmonth" value={allotMonth} />
                <input type="hidden" name="alotyear" value={allotYear} />
                <input type="hidden" name="dist_code" value={distCode} />
                <button
                  type="submit"
                  className="button"
                  style={{ marginLeft: "20px" }}
                >
                  {showButtonData?.buttonLabel}
                </button>
              </form>
            ) : (
              <button
                className="button"
                disabled={true}
                style={{
                  cursor: "default",
                  pointerEvents: "none",
                  marginLeft: "20px",
                }}
              >
                {showButtonData?.buttonLabel}
              </button>
            ))}
        </div>
      </div>
    </>
  );
};

export default CommissionNfsaTdsPtaxDetails;
